// src/pages/PostDetail/index.jsx
import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Typography, 
  Card, 
  List, 
  Avatar, 
  Form, 
  Button, 
  Input, 
  Tag, 
  Divider,
  message,
  Space,
  Tooltip,
  BackTop
} from 'antd';
import { 
  UserOutlined, 
  LikeOutlined, 
  EyeOutlined,
  CalendarOutlined,
  SendOutlined,
  ArrowLeftOutlined
} from '@ant-design/icons';
import AppHeader from '../../components/Header';
import AppFooter from '../../components/Footer';
import { mockPosts } from '../../api/mockData';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input; // 正确导入 TextArea

const PostDetail = ({ posts, loggedInUser, onLogout }) => {
  const [article, setArticle] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentForm] = Form.useForm();

  useEffect(() => {
    // 从 URL 获取文章 ID
    const pathParts = window.location.hash.split('/');
    const postId = pathParts[pathParts.length - 1];
    
    // 查找对应的文章
    const foundPost = mockPosts.find(p => p.id === postId);
    
    if (foundPost) {
      setArticle(foundPost);
      setComments(foundPost.comments || []);
    } else {
      // 如果没找到文章，返回首页
      window.location.hash = '#/';
    }
    
    setLoading(false);
  }, []);

  const handleSubmitComment = (values) => {
    const newComment = {
      id: comments.length + 1,
      author: loggedInUser?.name || "访客", // 显示登录用户名称
      avatar: "https://api.dicebear.com/7.x/miniavs/svg?seed=user",
      content: values.comment,
      datetime: new Date().toLocaleString('zh-CN'),
      likes: 0,
      replies: []
    };
    
    setComments([...comments, newComment]);
    commentForm.resetFields();
    message.success('评论发表成功！');
  };

  const handleReply = (commentId, replyContent) => {
    const updatedComments = comments.map(comment => {
      if (comment.id === commentId) {
        const newReply = {
          id: comment.replies.length + 1,
          author: loggedInUser?.name || "访客",
          content: replyContent,
          datetime: new Date().toLocaleString('zh-CN')
        };
        return {
          ...comment,
          replies: [...comment.replies, newReply]
        };
      }
      return comment;
    });
    
    setComments(updatedComments);
    message.success('回复成功！');
  };

  const handleLike = (commentId) => {
    const updatedComments = comments.map(comment => {
      if (comment.id === commentId) {
        return { ...comment, likes: comment.likes + 1 };
      }
      return comment;
    });
    setComments(updatedComments);
  };

  if (loading || !article) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <AppHeader />
        <Content style={{ padding: '20px 50px', marginTop: '64px' }}>
          <Card loading={true} style={{ maxWidth: '800px', margin: '0 auto' }} />
        </Content>
        <AppFooter />
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <AppHeader />
      
      <Content style={{ padding: '20px 50px', marginTop: '64px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          {/* 返回按钮 */}
          <Button 
            type="link" 
            icon={<ArrowLeftOutlined />} 
            onClick={() => window.history.back()}
            style={{ marginBottom: '16px' }}
          >
            返回
          </Button>
          
          {/* 文章标题和元信息 */}
          <Card style={{ marginBottom: '20px' }}>
            <Title level={2}>{article.title}</Title>
            
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '16px', marginBottom: '16px' }}>
              <Space>
                <Avatar size="large" style={{ backgroundColor: '#1890ff' }}>
                  {article.author.charAt(0)}
                </Avatar>
                <Text strong>{article.author}</Text>
              </Space>
              
              <Space>
                <CalendarOutlined />
                <Text type="secondary">{article.date}</Text>
              </Space>
              
              <Space>
                <EyeOutlined />
                <Text type="secondary">浏览 {article.views}</Text>
              </Space>
              
              <Space>
                <LikeOutlined />
                <Text type="secondary">点赞 {article.likes}</Text>
              </Space>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <Tag color="blue">{article.category}</Tag>
              {article.tags.map(tag => (
                <Tag key={tag} color="default">{tag}</Tag>
              ))}
            </div>
          </Card>
          
          {/* 文章内容 */}
          <Card style={{ marginBottom: '20px' }}>
            <div 
              style={{ 
                lineHeight: '1.8', 
                fontSize: '16px' 
              }}
              dangerouslySetInnerHTML={{ __html: article.content }} 
            />
          </Card>
          
          {/* 评论区域 */}
          <Card title="评论">
            {/* 发表评论表单 */}
            <Form form={commentForm} onFinish={handleSubmitComment} layout="vertical">
              <Form.Item 
                name="comment" 
                rules={[{ required: true, message: '请输入评论内容' }]}
              >
                <TextArea 
                  rows={4} 
                  placeholder="请输入您的评论..." 
                  style={{ borderRadius: '6px' }}
                />
              </Form.Item>
              
              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  icon={<SendOutlined />}
                  disabled={!loggedInUser} // 未登录用户不能评论
                >
                  {loggedInUser ? '发表评论' : '请先登录'}
                </Button>
              </Form.Item>
            </Form>
            
            <Divider />
            
            {/* 评论列表 */}
            <List
              dataSource={comments}
              itemLayout="vertical"
              header={`${comments.length} 条评论`}
              renderItem={comment => (
                <li style={{ padding: '16px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <Avatar 
                        src={comment.avatar} 
                        icon={<UserOutlined />}
                        style={{ backgroundColor: '#1890ff' }}
                      />
                      <div>
                        <Text strong>{comment.author}</Text>
                        <Text type="secondary" style={{ marginLeft: '8px' }}>
                          {comment.datetime}
                        </Text>
                      </div>
                    </div>
                    
                    <Paragraph style={{ marginTop: '8px' }}>{comment.content}</Paragraph>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                      <Tooltip title="点赞">
                        <Button 
                          type="text" 
                          size="small" 
                          icon={<LikeOutlined />}
                          onClick={() => handleLike(comment.id)}
                        >
                          {comment.likes}
                        </Button>
                      </Tooltip>
                    </div>
                    
                    {/* 回复列表 */}
                    {comment.replies && comment.replies.length > 0 && (
                      <div style={{ marginTop: '16px', paddingLeft: '40px' }}>
                        <Text strong style={{ fontSize: '14px', marginBottom: '8px' }}>
                          {comment.replies.length} 条回复
                        </Text>
                        <List
                          dataSource={comment.replies}
                          renderItem={reply => (
                            <li style={{ padding: '8px 0' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <Avatar 
                                  icon={<UserOutlined />}
                                  style={{ backgroundColor: '#52c41a' }}
                                />
                                <div>
                                  <Text strong>{reply.author}</Text>
                                  <Text type="secondary" style={{ marginLeft: '8px' }}>
                                    {reply.datetime}
                                  </Text>
                                </div>
                              </div>
                              <Paragraph style={{ marginTop: '4px', marginLeft: '40px' }}>
                                {reply.content}
                              </Paragraph>
                            </li>
                          )}
                        />
                      </div>
                    )}
                    
                    {/* 回复表单 */}
                    <div style={{ marginTop: '16px' }}>
                      <Form
                        onFinish={(values) => {
                          handleReply(comment.id, values.reply);
                        }}
                        layout="inline"
                      >
                        <Form.Item 
                          name="reply" 
                          rules={[{ required: true, message: '请输入回复内容' }]}
                          style={{ flex: 1, marginRight: '8px' }}
                        >
                          <Input placeholder="回复..." />
                        </Form.Item>
                        <Form.Item>
                          <Button 
                            type="primary" 
                            size="small" 
                            htmlType="submit"
                            disabled={!loggedInUser} // 未登录用户不能回复
                          >
                            回复
                          </Button>
                        </Form.Item>
                      </Form>
                    </div>
                  </div>
                </li>
              )}
            />
          </Card>
        </div>
        
        <BackTop />
      </Content>
      
      <AppFooter />
    </Layout>
  );
};

export default PostDetail;